import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, Button, TextField, Typography, Select, MenuItem, FormControl,
    Chip, Checkbox, FormControlLabel, Stack, Table, TableBody, TableRow, TableCell,
    Autocomplete
} from '@mui/material';
import * as api from './maingroupsApi';
import type {
    MainGroup, Brand, Department, Vat, Variant, VariantGroup,
    SubCategory, SalesChannel, Account, Intrastat, BusinessArea, TenantConfig
} from './maingroupsApi';
import { useDetailView } from '../common/useDetailView';
import { sx13, FormRow, DetailViewShell } from '../common/detailView';

type VisibilityValue = 'visible' | 'hidden';

interface VisibilityOption {
    value: VisibilityValue;
    description: string;
}

interface Props {
    mainGroupId: number | null;
}

export function MainGroupView({ mainGroupId }: Props) {
    const { t } = useTranslation();
    const detail = useDetailView<MainGroup>();

    const [allBrands, setAllBrands] = useState<Brand[]>([]);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [vats, setVats] = useState<Vat[]>([]);
    const [variants, setVariants] = useState<Variant[]>([]);
    const [variantGroups, setVariantGroups] = useState<VariantGroup[]>([]);
    const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
    const [salesChannels, setSalesChannels] = useState<SalesChannel[]>([]);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [intrastats, setIntrastats] = useState<Intrastat[]>([]);
    const [businessAreas, setBusinessAreas] = useState<BusinessArea[]>([]);
    const [tenantConfig, setTenantConfig] = useState<TenantConfig | null>(null);

    const [selectedBrands, setSelectedBrands] = useState<Brand[]>([]);

    const isNew = mainGroupId == null;

    const visibilityOptions: VisibilityOption[] = [
        { value: 'visible', description: t('common.visible') },
        { value: 'hidden', description: t('common.hidden') },
    ];

    const loadData = useCallback(async () => {
        await detail.withLoading(async () => {
            const [
                brandsData, departmentsData, vatsData, variantsData,
                variantGroupsData, subCategoriesData, salesChannelsData,
                accountsData, intrastatsData, businessAreasData, configData
            ] = await Promise.all([
                api.getBrands(),
                api.getDepartments(),
                api.getVats(),
                api.getVariants(),
                api.getVariantGroups(),
                api.getSubCategories(),
                api.getSalesChannels(),
                api.getAccounts(),
                api.getIntrastats(),
                api.getBusinessAreas(),
                api.getTenantConfig(),
            ]);

            setAllBrands(brandsData);
            setDepartments(departmentsData);
            setVats(vatsData);
            setVariants(variantsData);
            setVariantGroups(variantGroupsData);
            setSubCategories(subCategoriesData);
            setSalesChannels(salesChannelsData);
            setAccounts(accountsData);
            setIntrastats(intrastatsData);
            setBusinessAreas(businessAreasData);
            setTenantConfig(configData);

            if (isNew) {
                const defaultVisibility = {
                    intrastat: 'visible',
                    meursing: 'visible',
                    productHeight: 'visible',
                    productLength: 'visible',
                    productWeight: 'visible',
                    productWidth: 'visible',
                    storageLocation: 'visible',
                };
                const newMainGroup: MainGroup = {
                    id: 0,
                    mainGroupCode: '',
                    mainGroupDescription: '',
                    isVariant: true,
                    variant1: variantsData.find(v => v.id === configData.variant1) ?? null,
                    variant2: variantsData.find(v => v.id === configData.variant2) ?? null,
                    preferredVariantGroup2: null,
                    vat: null,
                    department: departmentsData[0] ?? null,
                    departments: [],
                    businessArea: null,
                    subCategories: [],
                    salesChannels: [],
                    salesAccount: null,
                    discountAccount: null,
                    intrastats: [],
                    brands: [],
                    itemGroups: [],
                    attributeVisibility: defaultVisibility,
                };
                detail.setItem(newMainGroup);
                detail.setIsNew(true);
                detail.setIsEditing(true);
            } else {
                const data = await api.getMainGroup(mainGroupId as number);
                if (!data) throw new Error(`MainGroup ${mainGroupId} returned empty response`);
                data.departments = data.departments ?? [];
                data.subCategories = data.subCategories ?? [];
                data.salesChannels = data.salesChannels ?? [];
                data.intrastats = data.intrastats ?? [];
                data.brands = data.brands ?? [];
                data.itemGroups = data.itemGroups ?? [];
                detail.setItem(data);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            }
        });
    }, [mainGroupId, isNew]);

    useEffect(() => { loadData(); }, [loadData]);

    const mainGroup = detail.item;

    // Validation
    const isMainGroupCodeValid = (): boolean => {
        if (!tenantConfig || !mainGroup?.mainGroupCode) return false;
        const isAssigned = tenantConfig.assignedMainGroupCodes.includes(mainGroup.mainGroupCode);
        const isNumeric = !isNaN(parseFloat(mainGroup.mainGroupCode)) && isFinite(Number(mainGroup.mainGroupCode));
        return !isAssigned && isNumeric && mainGroup.mainGroupCode.length === tenantConfig.articleMainGroupCodeLength;
    };

    const isMainGroupDescriptionValid = (): boolean =>
        !!mainGroup?.mainGroupDescription && mainGroup.mainGroupDescription.length > 0;

    const isMainGroupVatValid = (): boolean =>
        !!mainGroup?.vat && mainGroup.vat.id !== -1 && mainGroup.vat.id !== undefined;

    const isAllValid = (): boolean => {
        if (!detail.isNew) return isMainGroupDescriptionValid() && isMainGroupVatValid();
        return isMainGroupCodeValid();
    };

    // Available brands (not already on main group)
    const availableBrands = allBrands
        .filter(b => !(mainGroup?.itemGroups ?? []).some(ig => ig.brand.id === b.id))
        .sort((a, b) => a.brandCode.localeCompare(b.brandCode));

    // Filtered variants for variant1 (exclude variant2)
    const filteredVariants1 = variants.filter(v => v.id !== mainGroup?.variant2?.id);

    // Variant groups filtered by variant2
    const preferredVariantGroups2 = variantGroups.filter(
        vg => vg.variant?.id === mainGroup?.variant2?.id
    );

    // Actions
    const cancelEditing = () => {
        detail.cancelEdit();
        setSelectedBrands([]);
    };

    const saveMainGroup = async () => {
        if (!mainGroup) return;
        await detail.withSaving(async () => {
            if (detail.isNew) {
                const response = await api.createMainGroup(mainGroup, selectedBrands);
                detail.setItem(response);
                detail.setIsNew(false);
                detail.setIsEditing(false);
                setSelectedBrands([]);
            } else {
                const response = await api.updateMainGroup(mainGroup, selectedBrands);
                detail.setItem(response);
                detail.setIsEditing(false);
                setSelectedBrands([]);
            }
        });
    };

    const addBrands = async () => {
        if (selectedBrands.length === 0 || !mainGroup) return;
        await detail.withSaving(async () => {
            await api.createItemGroups([mainGroup], selectedBrands);
            const refreshed = await api.getMainGroup(mainGroup.id);
            refreshed.itemGroups = refreshed.itemGroups ?? [];
            detail.setItem(refreshed);
            setSelectedBrands([]);
        });
    };

    const updateVisibility = (key: keyof MainGroup['attributeVisibility'], value: string) => {
        if (!mainGroup) return;
        detail.update('attributeVisibility', {
            ...mainGroup.attributeVisibility,
            [key]: value,
        });
    };

    return (
        <DetailViewShell<MainGroup>
            featureKey="devFeature_reactMainGroupView"
            title={`${mainGroup?.mainGroupCode ?? ''} ${mainGroup?.mainGroupDescription ?? ''}`}
            isEditing={detail.isEditing}
            isNew={detail.isNew}
            saving={detail.saving}
            canSave={isAllValid()}
            loading={detail.loading}
            error={detail.error}
            item={mainGroup}
            onEdit={detail.startEdit}
            onSave={saveMainGroup}
            onCancel={cancelEditing}
            onClearError={() => detail.setError(null)}
        >
            {(item) => (
                <Box display="flex" gap={4} flexWrap="wrap">
                    {/* Left column */}
                    <Box flex={1} minWidth={400}>
                        <Stack spacing={2}>
                            {/* Main Group Code */}
                            <FormRow label={t('maingroup.maingroupcode')}>
                                {!detail.isNew ? (
                                    <Typography sx={sx13}>{item.mainGroupCode}</Typography>
                                ) : (
                                    <TextField
                                        size="small"
                                        fullWidth
                                        value={item.mainGroupCode}
                                        onChange={e => detail.update('mainGroupCode', e.target.value)}
                                        error={detail.isEditing && !isMainGroupCodeValid()}
                                    />
                                )}
                            </FormRow>

                            {/* Description */}
                            <FormRow label={t('maingroup.maingroupdescription')}>
                                {!detail.isEditing ? (
                                    <Typography sx={sx13}>{item.mainGroupDescription}</Typography>
                                ) : (
                                    <TextField
                                        size="small"
                                        fullWidth
                                        value={item.mainGroupDescription}
                                        onChange={e => detail.update('mainGroupDescription', e.target.value)}
                                        error={!isMainGroupDescriptionValid()}
                                    />
                                )}
                            </FormRow>

                            {/* Business Area */}
                            <FormRow label={t('maingroup.businessarea')}>
                                {!detail.isEditing ? (
                                    <Typography sx={sx13}>{item.businessArea?.name}</Typography>
                                ) : (
                                    <FormControl size="small" fullWidth>
                                        <Select
                                            value={item.businessArea?.id ?? ''}
                                            onChange={e => {
                                                const ba = businessAreas.find(b => b.id === e.target.value);
                                                if (ba) detail.update('businessArea', ba);
                                            }}
                                        >
                                            {businessAreas
                                                .sort((a, b) => a.name.localeCompare(b.name))
                                                .map(ba => (
                                                    <MenuItem key={ba.id} value={ba.id}>{ba.name}</MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </FormRow>

                            {/* Departments */}
                            <FormRow label={t('maingroup.departments')}>
                                {!detail.isEditing ? (
                                    <Box>
                                        {(item.departments ?? []).map(d => (
                                            <Typography sx={sx13} key={d.id}>{d.departmentDescription}</Typography>
                                        ))}
                                    </Box>
                                ) : (
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        options={departments.sort((a, b) => a.departmentDescription.localeCompare(b.departmentDescription))}
                                        getOptionLabel={o => o.departmentDescription}
                                        value={item.departments}
                                        onChange={(_, value) => detail.update('departments', value)}
                                        isOptionEqualToValue={(o, v) => o.id === v.id}
                                        renderInput={params => <TextField {...params} />}
                                        renderTags={(value, getTagProps) =>
                                            value.map((d, i) => <Chip {...getTagProps({ index: i })} key={d.id} label={d.departmentDescription} size="small" />)
                                        }
                                    />
                                )}
                            </FormRow>

                            {/* Sub Categories */}
                            <FormRow label={t('maingroup.subCategories')}>
                                {!detail.isEditing ? (
                                    <Box>
                                        {(item.subCategories ?? []).map(sc => (
                                            <Typography sx={sx13} key={sc.id}>{sc.subCategoryCode} {sc.subCategoryName}</Typography>
                                        ))}
                                    </Box>
                                ) : (
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        options={subCategories.sort((a, b) => a.subCategoryName.localeCompare(b.subCategoryName))}
                                        getOptionLabel={o => `${o.subCategoryCode} ${o.subCategoryName}`}
                                        value={item.subCategories}
                                        onChange={(_, value) => detail.update('subCategories', value)}
                                        isOptionEqualToValue={(o, v) => o.id === v.id}
                                        renderInput={params => <TextField {...params} />}
                                        renderTags={(value, getTagProps) =>
                                            value.map((sc, i) => <Chip {...getTagProps({ index: i })} key={sc.id} label={`${sc.subCategoryCode} ${sc.subCategoryName}`} size="small" />)
                                        }
                                    />
                                )}
                            </FormRow>

                            {/* Sales Channels */}
                            <FormRow label={t('maingroup.salesChannels')}>
                                {!detail.isEditing ? (
                                    <Box>
                                        {(item.salesChannels ?? []).map(sc => (
                                            <Typography sx={sx13} key={sc.id}>{sc.salesChannelCode} {sc.salesChannelDescription}</Typography>
                                        ))}
                                    </Box>
                                ) : (
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        options={salesChannels.sort((a, b) => a.salesChannelCode.localeCompare(b.salesChannelCode))}
                                        getOptionLabel={o => `${o.salesChannelCode} ${o.salesChannelDescription}`}
                                        value={item.salesChannels}
                                        onChange={(_, value) => detail.update('salesChannels', value)}
                                        isOptionEqualToValue={(o, v) => o.id === v.id}
                                        renderInput={params => <TextField {...params} />}
                                        renderTags={(value, getTagProps) =>
                                            value.map((sc, i) => (
                                                <Chip {...getTagProps({ index: i })} key={sc.id} label={`${sc.salesChannelCode} ${sc.salesChannelDescription}`} size="small" />
                                            ))
                                        }
                                    />
                                )}
                            </FormRow>

                            {/* VAT */}
                            <FormRow label={t('maingroup.vat')}>
                                {!detail.isEditing ? (
                                    <Typography sx={sx13}>{item.vat?.vatDescription}</Typography>
                                ) : (
                                    <FormControl size="small" fullWidth error={!isMainGroupVatValid()}>
                                        <Select
                                            value={item.vat?.id ?? ''}
                                            onChange={e => {
                                                const v = vats.find(vt => vt.id === e.target.value);
                                                if (v) detail.update('vat', v);
                                            }}
                                        >
                                            {vats
                                                .sort((a, b) => a.vatDescription.localeCompare(b.vatDescription))
                                                .map(v => (
                                                    <MenuItem key={v.id} value={v.id}>{v.vatDescription}</MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </FormRow>

                            {/* Sales Account */}
                            <FormRow label={t('maingroup.salesAccount')}>
                                {!detail.isEditing ? (
                                    <Typography sx={sx13}>
                                        {item.salesAccount?.accountNumber} {item.salesAccount?.accountName}
                                    </Typography>
                                ) : (
                                    <FormControl size="small" fullWidth>
                                        <Select
                                            value={item.salesAccount?.id ?? ''}
                                            onChange={e => {
                                                const a = accounts.find(acc => acc.id === e.target.value);
                                                detail.update('salesAccount', a ?? null);
                                            }}
                                        >
                                            <MenuItem value=""><em>&nbsp;</em></MenuItem>
                                            {accounts
                                                .sort((a, b) => a.accountNumber.localeCompare(b.accountNumber))
                                                .map(a => (
                                                    <MenuItem key={a.id} value={a.id}>{a.accountNumber} {a.accountName}</MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </FormRow>

                            {/* Discount Account */}
                            <FormRow label={t('maingroup.discountAccount')}>
                                {!detail.isEditing ? (
                                    <Typography sx={sx13}>
                                        {item.discountAccount?.accountNumber} {item.discountAccount?.accountName}
                                    </Typography>
                                ) : (
                                    <FormControl size="small" fullWidth>
                                        <Select
                                            value={item.discountAccount?.id ?? ''}
                                            onChange={e => {
                                                const a = accounts.find(acc => acc.id === e.target.value);
                                                detail.update('discountAccount', a ?? null);
                                            }}
                                        >
                                            <MenuItem value=""><em>&nbsp;</em></MenuItem>
                                            {accounts
                                                .sort((a, b) => a.accountNumber.localeCompare(b.accountNumber))
                                                .map(a => (
                                                    <MenuItem key={a.id} value={a.id}>{a.accountNumber} {a.accountName}</MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </FormRow>

                            {/* Is Variant */}
                            <FormRow label={t('maingroup.isvariant')}>
                                {!detail.isEditing ? (
                                    <Typography sx={sx13}>
                                        {item.isVariant ? t('common.true') : t('common.false')}
                                    </Typography>
                                ) : (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={item.isVariant}
                                                onChange={e => detail.update('isVariant', e.target.checked)}
                                                size="small"
                                            />
                                        }
                                        label=""
                                    />
                                )}
                            </FormRow>

                            {/* Variant 1 */}
                            {item.isVariant && (
                                <FormRow label={t('maingroup.variant1')}>
                                    {!detail.isEditing ? (
                                        <Typography sx={sx13}>{item.variant1?.variantName}</Typography>
                                    ) : (
                                        <FormControl size="small" fullWidth>
                                            <Select
                                                value={item.variant1?.id ?? ''}
                                                onChange={e => {
                                                    const v = variants.find(vr => vr.id === e.target.value);
                                                    if (v) detail.update('variant1', v);
                                                }}
                                            >
                                                {filteredVariants1.map(v => (
                                                    <MenuItem key={v.id} value={v.id}>{v.variantName}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                </FormRow>
                            )}

                            {/* Preferred Variant Group 2 */}
                            {item.isVariant && (
                                <FormRow label={item.variant2?.variantName ?? t('maingroup.preferredVariantGroup2')}>
                                    {!detail.isEditing ? (
                                        <Typography sx={sx13}>{item.preferredVariantGroup2?.groupName}</Typography>
                                    ) : (
                                        <FormControl size="small" fullWidth>
                                            <Select
                                                value={item.preferredVariantGroup2?.id ?? ''}
                                                onChange={e => {
                                                    const vg = preferredVariantGroups2.find(g => g.id === e.target.value);
                                                    detail.update('preferredVariantGroup2', vg ?? null);
                                                }}
                                            >
                                                <MenuItem value=""><em>&nbsp;</em></MenuItem>
                                                {preferredVariantGroups2
                                                    .sort((a, b) => a.groupName.localeCompare(b.groupName))
                                                    .map(vg => (
                                                        <MenuItem key={vg.id} value={vg.id}>{vg.groupName}</MenuItem>
                                                    ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                </FormRow>
                            )}

                            {/* Intrastats */}
                            {!detail.isNew && intrastats.length > 0 && (
                                <FormRow label={t('maingroup.intrastatnumber')}>
                                    {!detail.isEditing ? (
                                        <Box>
                                            {(item.intrastats ?? []).map(ist => (
                                                <Typography sx={sx13} key={ist.id}>{ist.code} {ist.internalDescription}</Typography>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Autocomplete
                                            multiple
                                            size="small"
                                            options={intrastats.sort((a, b) => a.code.localeCompare(b.code))}
                                            getOptionLabel={o => `${o.code} ${o.internalDescription}`}
                                            value={item.intrastats}
                                            onChange={(_, value) => detail.update('intrastats', value)}
                                            isOptionEqualToValue={(o, v) => o.id === v.id}
                                            renderInput={params => <TextField {...params} />}
                                            renderTags={(value, getTagProps) =>
                                                value.map((ist, i) => (
                                                    <Chip {...getTagProps({ index: i })} key={ist.id} label={`${ist.code} ${ist.internalDescription}`} size="small" />
                                                ))
                                            }
                                        />
                                    )}
                                </FormRow>
                            )}

                            {/* Brands */}
                            {!detail.isNew && (
                                <>
                                    <Box />
                                    <FormRow label={t('maingroup.brands')}>
                                        <Stack spacing={1}>
                                            {detail.isEditing && (
                                                <Box display="flex" gap={1}>
                                                    <Autocomplete
                                                        multiple
                                                        size="small"
                                                        fullWidth
                                                        options={availableBrands}
                                                        getOptionLabel={o => `${o.brandCode} ${o.brandDescription}`}
                                                        value={selectedBrands}
                                                        onChange={(_, value) => setSelectedBrands(value)}
                                                        isOptionEqualToValue={(o, v) => o.id === v.id}
                                                        renderInput={params => <TextField {...params} placeholder={t('article.addBrand')} />}
                                                    />
                                                    {selectedBrands.length > 0 && (
                                                        <Button variant="outlined" color="inherit" size="small" onClick={addBrands} disabled={detail.saving}>
                                                            +
                                                        </Button>
                                                    )}
                                                </Box>
                                            )}
                                            <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
                                                {(item.itemGroups ?? [])
                                                    .sort((a, b) => a.brand.brandDescription.localeCompare(b.brand.brandDescription))
                                                    .map(ig => (
                                                        <Typography sx={sx13} key={ig.brand.id}>
                                                            {ig.brand.brandCode} {ig.brand.brandDescription}
                                                        </Typography>
                                                    ))}
                                            </Box>
                                        </Stack>
                                    </FormRow>
                                </>
                            )}
                        </Stack>
                    </Box>

                    {/* Right column - Attribute Visibility */}
                    <Box flex={1} minWidth={300}>
                        <Typography sx={sx13} fontWeight="bold" mb={1}>
                            {t('maingroup.showAttributes')}:
                        </Typography>
                        <Table size="small">
                            <TableBody>
                                {([
                                    { key: 'productLength' as const, label: t('article.length') },
                                    { key: 'productWidth' as const, label: t('article.width') },
                                    { key: 'productHeight' as const, label: t('article.height') },
                                    { key: 'productWeight' as const, label: t('article.weight') },
                                    { key: 'intrastat' as const, label: t('article.intrastatnumber') },
                                    { key: 'meursing' as const, label: t('article.meursing') },
                                    { key: 'storageLocation' as const, label: t('article.storageLocation') },
                                ]).map(({ key, label }) => (
                                    <TableRow key={key}>
                                        <TableCell sx={sx13}>{label}</TableCell>
                                        <TableCell>
                                            <FormControl size="small" fullWidth disabled={!detail.isEditing}>
                                                <Select
                                                    value={item.attributeVisibility[key] ?? 'visible'}
                                                    onChange={e => updateVisibility(key, e.target.value)}
                                                    sx={sx13}
                                                >
                                                    {visibilityOptions.map(opt => (
                                                        <MenuItem key={opt.value} value={opt.value}>{opt.description}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                </Box>
            )}
        </DetailViewShell>
    );
}
